import {
    Autocomplete,
    Box,
    CircularProgress,
    FormControl,
    FormHelperText,
    TextField,
    Typography,
} from '@mui/material'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { useAppSelector } from '../../redux/hook'
import { UserApiResponse } from '../UserManagement/interfaces'
import { fetchUsers } from '../../services/userServices'
import TruncatedText from '../Common/TruncatedText'
import { UserStatus } from '../../constants/constants'

interface SelectUserProps {
    id: string
    label: string
    value: string
    onChange: any
    isLegend?: boolean
    placeholder?: string
    required?: boolean
    disabled?: boolean
    errorMessage?: string
    ui_width?: string
    list_Height?: number
}

const SelectUser = (props: SelectUserProps): ReactElement => {
    const {
        id,
        label,
        value = '',
        onChange,
        isLegend = false,
        placeholder,
        required,
        disabled,
        errorMessage = '',
        list_Height,
    } = props

    const org = useAppSelector((state) => state.org.org)

    const [selectedUser, setSelectedUser] = useState<string>('')
    const [usersList, setUsersList] = useState<any[]>([])

    const [searchText, setSearchText] = useState<string>('')
    const [loading, setLoading] = useState<boolean>(false)
    const [page, setPage] = useState<number>(1)
    const [hasMore, setHasMore] = useState<boolean>(true)

    const getUsersList = useCallback(async () => {
        if (!hasMore) return
        setLoading(true)
        const response = (await fetchUsers({
            pageNumber: page,
            size: 10,
            searchText: searchText?.trim(),
            filterBy: '',
            sortOrder: '',
            orgId: org!.id,
        })) as UserApiResponse
        const updatedGetUsers =
            response?.usersModels?.filter(
                (i) => i.status?.toLowerCase() === UserStatus.ACTIVE,
            ) || []

        setUsersList((prev) =>
            page === 1
                ? [...(updatedGetUsers || [])]
                : [...prev, ...(updatedGetUsers || [])],
        )
        setHasMore(updatedGetUsers?.length > 0)
        setLoading(false)
    }, [hasMore, page, org, searchText])

    useEffect(() => {
        getUsersList()
    }, [getUsersList])

    useEffect(() => {
        setSelectedUser(value)
    }, [value])

    const onInputChange = (
        event: React.ChangeEvent<object>,
        newValue: string,
    ) => {
        setSearchText(newValue)
        setPage(1)
        setHasMore(true)
    }

    const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
        const listboxNode = event.currentTarget
        if (
            listboxNode.scrollHeight - listboxNode.scrollTop <=
                listboxNode.clientHeight + 5 &&
            !loading &&
            hasMore
        ) {
            setPage((prev) => prev + 1)
        }
    }

    const onUserSelect = (event: React.ChangeEvent<object>, user: any) => {
        if (user) {
            setSelectedUser(user?.email)
            onChange(id, user?.email)
        }
    }

    const onAutocompleteBlur = () => {
        setSelectedUser(value)
        setPage(1)
        setSearchText('')
        setHasMore(true)
    }

    return (
        <Box sx={{ width: '100%' }}>
            <FormControl fullWidth disabled={!!disabled} size="small">
                {isLegend ? null : (
                    <Typography sx={{ opacity: '0.7', fontSize: '14px' }}>
                        {label}
                        {required ? '*' : ''}
                    </Typography>
                )}
                <Autocomplete
                    id={`${id}-user-select`}
                    loading={loading}
                    disableClearable
                    options={usersList}
                    getOptionLabel={(option) => option.email || selectedUser}
                    filterOptions={(x) => x}
                    ListboxProps={{
                        style: { maxHeight: list_Height ?? 200 },
                        onScroll: handleScroll, // Attach scroll event
                    }}
                    renderOption={(props: any, option) => {
                        const { key, ...optionProps } = props
                        return (
                            <Box
                                key={key}
                                component="li"
                                sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                                {...optionProps}
                            >
                                <TruncatedText text={option.email} />
                            </Box>
                        )
                    }}
                    value={selectedUser}
                    isOptionEqualToValue={(option, value) =>
                        option.email === value
                    }
                    sx={{
                        width: '100%',
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        '& .MuiOutlinedInput-root': {
                            padding: '2.5px 4px 2.5px 5px',
                        },
                    }}
                    onInputChange={onInputChange}
                    onChange={onUserSelect}
                    onBlur={onAutocompleteBlur}
                    size="small"
                    renderInput={(params: any) => {
                        return (
                            <TextField
                                {...params}
                                value={selectedUser}
                                label={
                                    isLegend
                                        ? label + `${required ? ' *' : ''}`
                                        : ''
                                }
                                size="small"
                                placeholder={placeholder ?? null}
                                error={errorMessage !== ''}
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loading ? (
                                                    <CircularProgress
                                                        color="inherit"
                                                        size={20}
                                                    />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    },
                                }}
                            />
                        )
                    }}
                />
                {errorMessage !== '' && (
                    <FormHelperText sx={{ color: 'red' }}>
                        {errorMessage}
                    </FormHelperText>
                )}
            </FormControl>
        </Box>
    )
}

export default SelectUser
